import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ToastComponent } from '@ista-ui/angular';
import type { IndividualConfig } from 'ngx-toastr';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { NotificationService } from './notification.service';
import { NotificationLiveTime, NotificationType, ToastNotification } from './notification';

@Component({
  selector: 'ic-toasts',
  template: ` <!-- TOASTS COMPONENT --> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ToastsComponent implements OnInit, OnDestroy {
  // injectables
  private readonly notifications = inject(NotificationService);
  private readonly toastr = inject(ToastrService);
  private readonly translation = inject(TranslateService);

  private subscription = new Subscription();

  public ngOnInit(): void {
    this.subscribeToNotifications();
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private subscribeToNotifications() {
    const onHandled = this.notifications.notifications$
      .pipe(filter((n) => n instanceof ToastNotification))
      .subscribe(async (notification: ToastNotification) => {
        const message = notification.message
          ? notification.message
          : await this.localizeMessage(notification.messageKey, notification.messageParams);
        const config = { ...this.buildToastPayload(notification.liveTime) };

        this.toastr.show(message, '', config, NotificationType[notification.type].toLowerCase());
      });

    this.subscription.add(onHandled);
  }

  private buildToastPayload(liveTime?: NotificationLiveTime): Partial<IndividualConfig> {
    const config: Partial<IndividualConfig> = {
      closeButton: true,
      toastComponent: ToastComponent,
      payload: {
        showIcon: true,
      },
    };

    switch (liveTime) {
      case NotificationLiveTime.LONG: {
        return Object.assign(config, { timeOut: 20 * 1000 });
      }
      case NotificationLiveTime.UNTIL_TAPPED: {
        return Object.assign(config, { disableTimeOut: true });
      }
      default:
      case NotificationLiveTime.NORMAL: {
        return config;
      }
    }
  }

  private async localizeMessage(messageKey: string, messageParams?: { [key: string]: string }): Promise<string> {
    return firstValueFrom(this.translation.get(messageKey, messageParams));
  }
}
